// Swap-meal picker (Phase 0009).
//
// Displays candidates returned by list_safe_replacement_candidates.
// The browser renders exactly the rows the server returned — no
// client-side allergen / restriction / Kids / eligibility filtering.
//
// Layering (Phase B8 fix):
//   * The modal is portalled to document.body so no ancestor's
//     transform/filter can break its position: fixed.
//   * The backdrop is a solid, opaque overlay above the app's sticky
//     header (which uses z-50). We use z-index: 1000 for the wrapper.
//   * The dialog surface uses a defined solid palette colour (the
//     `--paper` variable was never declared in index.css; falling back
//     to that undefined variable made the surface transparent and
//     caused the reported bleed-through). We now use `--cream` for
//     the panel background and a darker rgba for the backdrop.
//   * Body scroll is locked while the modal is open and restored on
//     close/unmount.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrandButton } from './BrandButton';
import {
  rpcListSafeReplacementCandidates,
  readCandidates,
} from '../lib/planPersistence';

function fmtInt(n) { return Number.isFinite(n) ? Math.round(n).toString() : '—'; }
function fmtDec1(n) { return Number.isFinite(n) ? (Math.round(n * 10) / 10).toString() : '—'; }

// Lock the document body scroll while at least one modal is mounted.
// A tiny reference counter prevents nested/rapid mounts from leaking
// a permanent lock.
let scrollLockCount = 0;
let savedOverflow = null;
let savedPaddingRight = null;
function lockBodyScroll() {
  if (typeof document === 'undefined') return;
  if (scrollLockCount === 0) {
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    // Preserve scroll-bar width to avoid layout jump when the bar
    // disappears.
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) {
      document.body.style.paddingRight = `${sbw}px`;
    }
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount += 1;
}
function unlockBodyScroll() {
  if (typeof document === 'undefined') return;
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedOverflow || '';
    document.body.style.paddingRight = savedPaddingRight || '';
    savedOverflow = null;
    savedPaddingRight = null;
  }
}

export function SwapMealPicker({
  open,
  currentItem,
  planId,
  generationId,
  onCancel,
  onPick,
  submitting,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);

  // Load candidates on open.
  useEffect(() => {
    if (!open || !currentItem) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    rpcListSafeReplacementCandidates({
      generation_id: generationId,
      plan_id: planId,
      day_number: currentItem.day_number,
      slot: currentItem.slot,
      component_order: currentItem.component_order,
    }).then((r) => {
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error);
        setCandidates([]);
      } else {
        setCandidates(readCandidates(r.data));
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, currentItem, planId, generationId]);

  // Body scroll lock (and Escape-to-close) while open.
  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onCancel && onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockBodyScroll();
    };
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Swap this meal"
      className="fixed inset-0 flex items-center justify-center p-4"
      // Explicit z-index above the app's sticky header (which uses
      // Tailwind z-50 = 50). Using an inline value avoids relying on
      // an undocumented Tailwind class.
      style={{
        zIndex: 1000,
        background: 'rgba(15, 14, 12, 0.62)', // solid overlay (ink @ 62%)
      }}
      onClick={(e) => {
        // Backdrop click closes (matches existing "Cancel" behaviour)
        // when not mid-submit and only when the click hit the backdrop
        // itself, not a bubbled event from the panel.
        if (e.target === e.currentTarget && !submitting) {
          onCancel && onCancel();
        }
      }}
      data-testid="swap-picker-overlay"
    >
      <div
        className="w-full max-w-2xl p-5"
        style={{
          // Use a defined palette colour so the panel is fully opaque.
          background: 'var(--cream, #f7f0e2)',
          color: 'var(--ink, #0f0e0c)',
          borderRadius: 'var(--radius, 10px)',
          border: '1px solid var(--line, rgba(0,0,0,0.12))',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          // Constrain the panel and let the list scroll internally.
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="swap-picker"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap" style={{ flex: '0 0 auto' }}>
          <div className="min-w-0">
            <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>
              Swap this meal
            </div>
            <h2 className="h3 mt-1" data-testid="swap-picker-title">
              Replace {currentItem?.product_name || 'this meal'}
            </h2>
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-2)' }}>
              We&rsquo;re only showing meals that fit your allergies and preferences.
            </p>
          </div>
          <BrandButton
            type="button" variant="ghost" size="sm"
            onClick={onCancel} disabled={submitting}
            data-testid="swap-picker-cancel-btn"
          >
            Cancel
          </BrandButton>
        </div>

        {/* Scrollable body — internal only, page beneath stays locked. */}
        <div
          className="mt-4"
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {loading && (
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}
               data-testid="swap-picker-loading">
              Loading options…
            </p>
          )}

          {error && !loading && (
            <div
              className="p-3"
              style={{
                background: 'var(--warning-bg, rgba(178,106,0,0.12))',
                border: '1px solid var(--warning, #b26a00)',
                borderRadius: 'var(--radius, 10px)',
              }}
              data-testid="swap-picker-error"
            >
              <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{error}</p>
            </div>
          )}

          {!loading && !error && candidates.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}
               data-testid="swap-picker-empty">
              No safe replacements available right now.
            </p>
          )}

          {!loading && !error && candidates.length > 0 && (
            <ul className="divide-y" style={{ borderTop: '1px solid var(--line)' }}
                data-testid="swap-picker-list">
              {candidates.map((c) => (
                <li
                  key={c.etm_id}
                  className="py-3 flex items-start justify-between gap-3 flex-wrap"
                  style={{ borderBottom: '1px solid var(--line)' }}
                  data-testid={`swap-candidate-${c.etm_id}`}
                >
                  <div className="min-w-0" style={{ flex: '1 1 260px' }}>
                    <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>
                      {c.etm_id}
                    </div>
                    <div className="font-display" style={{ color: 'var(--ink)' }}
                         data-testid={`swap-candidate-name-${c.etm_id}`}>
                      {c.product_name || '—'}
                    </div>
                    {c.serving_size && (
                      <div className="mt-1 text-xs" style={{ color: 'var(--text-dim)' }}>
                        Serving: {c.serving_size}
                      </div>
                    )}
                    {c.major_allergens && (
                      <div className="mt-1 text-xs" style={{ color: 'var(--text-dim)' }}>
                        Contains: {c.major_allergens}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="grid grid-cols-4 gap-x-3 text-sm" style={{ color: 'var(--ink-2)' }}>
                      <div>
                        <div className="eyebrow whitespace-nowrap"
                             style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Cal</div>
                        <div>{fmtInt(c.calories)}</div>
                      </div>
                      <div>
                        <div className="eyebrow whitespace-nowrap"
                             style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Prot</div>
                        <div>{fmtDec1(c.protein)} g</div>
                      </div>
                      <div>
                        <div className="eyebrow whitespace-nowrap"
                             style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Carb</div>
                        <div>{fmtDec1(c.carbohydrates)} g</div>
                      </div>
                      <div>
                        <div className="eyebrow whitespace-nowrap"
                             style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Fat</div>
                        <div>{fmtDec1(c.fat)} g</div>
                      </div>
                    </div>
                    <BrandButton
                      type="button" variant="primary" size="sm"
                      onClick={() => onPick(c)}
                      disabled={submitting}
                      data-testid={`swap-candidate-pick-${c.etm_id}`}
                    >
                      Use this
                    </BrandButton>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  // Portal to <body> so no ancestor transform / filter / overflow can
  // clip or reposition the modal.
  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
